import _ from 'lodash'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  getEnrollmentData,
  getRegistration,
  initializePayment,
  quotePayment,
  refreshEnrollmentData,
  requestEnrollment,
} from '../actions/programRegistrationActions'
import { createUser, inviteFamilyMember } from '../actions/authActions'
import { REGISTRATION_STAGES, REGISTRATION_STAGE_BY_STEP } from '../misc/Constants'

const initialState = {
  status: 'inactive',
  errors: null,
  newUserErrors: {},
  enrollmentData: null,
  invitationToken: null,
  registrationStatus: null,
  selectedFamilyMember: null,
  selectedPackage: {},
  selectedPaymentMethod: null,
  selectedMembershipPlanId: null,
  paymentError: null,
  billingAddressError: null,
  paymentSession: null,
  acceptedQuote: null,
  isProcessingPayment: false,
  acceptedMaterials: {},
  refusedMaterials: {},
  completedSteps: [],
  currentStep: null,
  isEmbedded: false,
  courseId: null,
}

const programRegistrationSlice = createSlice({
  name: 'programRegistration',
  initialState,
  reducers: {
    resetRegistration: () => initialState,
    decrementCurrentStep: (state) => {
      state.currentStep--
    },
    incrementCurrentStep: (state) => {
      state.currentStep++
    },
    incrementCurrentStepBy: (state, action) => {
      state.currentStep += action.payload
    },
    completeStep: (state, action) => {
      state.completedSteps.push(action.payload)
    },
    setSelectedFamilyMember: (state, action) => {
      state.selectedFamilyMember = action.payload
    },
    setSelectedPackage: (state, action) => {
      state.selectedPackage = action.payload
    },
    setSelectedPaymentMethod: (state, action) => {
      state.selectedPaymentMethod = action.payload
      state.paymentError = null
    },
    setSelectedMembershipPlanId: (state, action) => {
      state.selectedMembershipPlanId = action.payload
    },
    setPaymentError: (state, action) => {
      state.paymentError = action.payload
    },
    resetPaymentSession: (state) => {
      state.paymentSession = null
    },
    setBillingAddress: (state, action) => {
      state.billingAddressError = null
      if (state.paymentSession) {
        state.paymentSession.billingAddress = action.payload
      }
      if (state.paymentSession?.quote) {
        state.paymentSession.quote.status = 'loading'
      }
    },
    setBillingAddressError: (state, action) => {
      state.billingAddressError = action.payload
    },
    setIsProcessingPayment: (state, action) => {
      state.isProcessingPayment = action.payload
    },
    addAcceptedMaterial: (state, action) => {
      const material = action.payload
      state.acceptedMaterials[material.id] = material
      delete state.refusedMaterials[material.id]
    },
    addRefusedMaterial: (state, action) => {
      const material = action.payload
      state.refusedMaterials[material.id] = material
      delete state.acceptedMaterials[material.id]
    },
  },
  extraReducers: {
    // Get enrollment data
    [getEnrollmentData.pending]: (state, action) => {
      state.status = 'loading enrollment data'
      state.invitationToken = action.meta.arg.invitationToken
    },
    [getEnrollmentData.fulfilled]: (state, action) => {
      state.status = 'active'
      const enrollmentData = action.payload.enrollmentData
      state.enrollmentData = enrollmentData
      state.currentStep = 0
    },
    [getEnrollmentData.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
    },

    // A new junior golfer is seated on the family's membership as they are created, so who holds a seat has to come
    // from the server again. Only the data moves: the screen keeps its step and never drops back to its loader.
    [refreshEnrollmentData.fulfilled]: (state, action) => {
      state.enrollmentData = action.payload.enrollmentData
    },

    // Request enrollment
    [requestEnrollment.pending]: state => {
      state.status = 'processing registration'
    },
    [requestEnrollment.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
    },

    // Initialize payment (pending registration, no Stripe call)
    [initializePayment.pending]: (state, action) => {
      state.selectedMembershipPlanId = null
      state.billingAddressError = null
      state.paymentSession = {
        requestId: action.meta.requestId,
        order: action.meta.arg,
        status: 'loading',
        registrationId: null,
        billingAddress: null,
        quote: null,
      }
    },
    [initializePayment.fulfilled]: (state, action) => {
      if (state.paymentSession?.requestId === action.meta.requestId) {
        state.paymentSession.status = 'ready'
        state.paymentSession.registrationId = action.payload.registrationId
      }
    },
    [initializePayment.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
      state.paymentSession = null
    },

    // Quote payment (pricing lines + membership decision)
    [quotePayment.pending]: (state, action) => {
      if (state.paymentSession) {
        // a re-quote keeps the previous answer so the mounted Stripe card form survives it
        const previous = state.paymentSession.quote

        state.paymentSession.quote = {
          requestId: action.meta.requestId,
          params: {
            paymentMethod: action.meta.arg.paymentMethod,
            membershipPlanId: action.meta.arg.membershipPlanId ?? null,
          },
          status: 'loading',
          pricing: previous?.pricing ?? null,
          cardRequired: previous?.cardRequired ?? false,
          membership: previous?.membership ?? null,
          stripeAccountId: previous?.stripeAccountId ?? null,
          addressRequired: previous?.addressRequired ?? false,
        }
      }
    },
    [quotePayment.fulfilled]: (state, action) => {
      const quote = state.paymentSession?.quote

      if (quote?.requestId === action.meta.requestId) {
        state.billingAddressError = null
        quote.status = 'ready'
        quote.pricing = action.payload.pricing
        quote.cardRequired = action.payload.cardRequired
        quote.membership = action.payload.membership
        quote.stripeAccountId = action.payload.stripeAccountId
        quote.addressRequired = action.payload.addressRequired
      }
    },
    [quotePayment.rejected]: (state, action) => {
      const quote = state.paymentSession?.quote

      if (action.payload?.notPayable) {
        state.paymentSession = null
      } else if (action.payload?.invalidBillingAddress) {
        state.billingAddressError = action.payload.invalidBillingAddress[0]
        // the refused address moved no amount, so the answer the payer is looking at still stands
        if (quote?.pricing) {
          quote.status = 'ready'
        }
      } else {
        state.status = 'error'
        state.errors = action.payload
      }
    },

    // Get registration (success screen)
    [getRegistration.pending]: state => {
      state.status = 'processing registration'
    },
    [getRegistration.fulfilled]: (state, action) => {
      const registration = action.payload.registration

      if (registration.status === 'pending') {
        state.status = 'processing registration'
      } else {
        state.status = 'finished'
        state.registrationStatus = registration.status
        state.acceptedMaterials = registration.materials
        state.selectedPackage = registration.package
        state.selectedFamilyMember = registration.user
        state.selectedPaymentMethod = registration.paymentMethod
        state.acceptedQuote = registration.pricing ? { pricing: registration.pricing, membership: registration.membership } : null
        state.courseId = registration.courseId
        state.isEmbedded = registration.createdBy === 'mobile_registration'
      }
    },
    [getRegistration.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
    },

    // Create new user
    [createUser.pending]: state => {
      state.status = 'creating user'
    },
    [createUser.fulfilled]: (state, action) => {
      const user = action.payload.user

      state.enrollmentData.familyMembers.push(user)
      state.selectedFamilyMember = user
      state.status = 'active'
    },
    [createUser.rejected]: state => {
      state.status = 'active'
    },

    // Invite family member
    [inviteFamilyMember.pending]: state => {
      state.status = 'creating user'
    },
    [inviteFamilyMember.fulfilled]: (state, action) => {
      const user = action.payload.user

      state.enrollmentData.familyMembers.push(user)
      state.status = 'active'
    },
    [inviteFamilyMember.rejected]: state => {
      state.status = 'active'
    },
  },
})

export const {
  resetRegistration,
  decrementCurrentStep,
  incrementCurrentStep,
  incrementCurrentStepBy,
  completeStep,
  setSelectedFamilyMember,
  setSelectedPackage,
  setSelectedPaymentMethod,
  setSelectedMembershipPlanId,
  setPaymentError,
  resetPaymentSession,
  setBillingAddress,
  setBillingAddressError,
  setIsProcessingPayment,
  addAcceptedMaterial,
  addRefusedMaterial,
} = programRegistrationSlice.actions

export const selectAcceptedMaterialsIds = createSelector(
  state => state.programRegistration.acceptedMaterials,
  acceptedMaterials => _.map(Object.keys(acceptedMaterials), Number)
)

export const selectRefusedMaterialsIds = createSelector(
  state => state.programRegistration.refusedMaterials,
  refusedMaterials => _.map(Object.keys(refusedMaterials), Number)
)

export const selectRegistrationOrder = createSelector(
  state => state.programPresentation.course?.id,
  state => state.programRegistration.selectedFamilyMember?.id,
  state => state.programRegistration.selectedPackage,
  selectAcceptedMaterialsIds,
  (courseId, userId, selectedPackage, materialsIds) => ({
    courseId,
    userId,
    packageId: selectedPackage.id,
    packagePrice: selectedPackage.price,
    materialsIds,
  })
)

export const selectConsumerPlan = state => state.programPresentation.course?.community?.licenseType === 'consumer_plan'

export const selectQuote = state => state.programRegistration.paymentSession?.quote

const COVERING_MEMBERSHIP_STATUSES = ['active', 'past_due']

export const MEMBERSHIP_DUE_ACTIONS = ['purchase', 'upgrade']

export const selectMembershipCovered = state =>
  COVERING_MEMBERSHIP_STATUSES.includes(state.programPresentation.course?.membership?.family?.status)

export const selectMembershipUnavailableOnCourse = state => {
  const membership = state.programPresentation.course?.membership

  return !!selectConsumerPlan(state) && !!membership && !selectMembershipCovered(state) && _.isEmpty(membership.plans)
}

export const selectElementsSetup = createSelector(
  selectQuote,
  (quote) => {
    // read off any answer that has arrived, not only the current one: a re-quote must not remount the card form
    if (!quote?.pricing || !quote.cardRequired) {
      return null
    }

    const membershipDue = MEMBERSHIP_DUE_ACTIONS.includes(quote.membership?.action)
    const chargedKey = membershipDue ? 'membership' : 'program_fee'
    const chargedLine = quote.pricing.lines.find(line => line.key === chargedKey)

    if (!chargedLine) {
      return null
    }

    return {
      stripeAccountId: membershipDue ? null : quote.stripeAccountId,
      mode: membershipDue ? 'subscription' : 'payment',
      captureMethod: membershipDue ? null : 'manual',
      amount: chargedLine.stripeAmount,
      currency: chargedLine.currency.code,
    }
  }
)

export const selectQuoteParams = createSelector(
  state => state.programRegistration.selectedPaymentMethod,
  state => state.programRegistration.selectedMembershipPlanId,
  (paymentMethod, membershipPlanId) => ({ paymentMethod, membershipPlanId })
)

export const selectRegistrationSteps = createSelector(
  state => state.programPresentation.course?.materials,
  selectConsumerPlan,
  state => selectQuote(state)?.membership?.action,
  (materials, consumerPlan, membershipAction) => [
    'programDetails',
    ...(_.isEmpty(materials) ? [] : ['programMaterials']),
    ...(consumerPlan && (!membershipAction || MEMBERSHIP_DUE_ACTIONS.includes(membershipAction)) ? ['membership'] : []),
    'checkout',
  ]
)

export const selectRegistrationStage = createSelector(
  state => state.programRegistration.status,
  selectRegistrationSteps,
  state => state.programRegistration.currentStep,
  (status, steps, currentStep) => {
    // the completed page is not one of the screens, so it reports the last stage directly
    if (status === 'finished') {
      return REGISTRATION_STAGES.length - 1
    }

    const stage = REGISTRATION_STAGE_BY_STEP[steps[currentStep]]

    return stage ? REGISTRATION_STAGES.indexOf(stage) : 0
  }
)

// How far the payer has got, which never goes back when they do: the stepper's ticks read this, not the position.
export const selectCompletedRegistrationStages = createSelector(
  state => state.programRegistration.status,
  selectRegistrationSteps,
  state => state.programRegistration.completedSteps,
  (status, steps, completedSteps) => {
    if (status === 'finished') {
      return REGISTRATION_STAGES.length
    }

    const firstUnfinished = REGISTRATION_STAGES.findIndex(stage => steps.some(
      (step, index) => REGISTRATION_STAGE_BY_STEP[step] === stage && !completedSteps.includes(index)
    ))

    return firstUnfinished === -1 ? REGISTRATION_STAGES.length : firstUnfinished
  }
)

export const withMaterials = createSelector(
  state => state.programRegistration.acceptedMaterials,
  acceptedMaterials => !_.isEmpty(acceptedMaterials)
)

export const validProgramDetailsStep = createSelector(
  state => ({
    selectedFamilyMember: state.programRegistration.selectedFamilyMember,
    selectedPackage: state.programRegistration.selectedPackage,
    coursePackages: state.programRegistration.enrollmentData?.coursePackages,
  }),
  programDetailsData => programDetailsData.selectedFamilyMember
    && (!_.isEmpty(programDetailsData.selectedPackage) || programDetailsData.coursePackages?.length === 0)
)

export const validProgramMaterialsStep = createSelector(
  selectAcceptedMaterialsIds,
  selectRefusedMaterialsIds,
  state => state.programRegistration.enrollmentData?.courseMaterials,
  (acceptedMaterialsIds, refusedMaterialsIds, courseMaterials) =>
    acceptedMaterialsIds?.length + refusedMaterialsIds?.length === courseMaterials?.length
)

export default programRegistrationSlice.reducer
